// exports Boundary, Curve, Polyline, Segment, Quadratic, Cubic

import { map, last } from 'lodash';

import { OwnedObject, areEqual, cloneDeep } from 'gear';

import { Point } from './point';
import { Bounds, segmentBounds, bezierBounds } from './bounds'; // [TODO] Bring segmentBounds here
import { Cubic } from './bezier';

import { Xml } from '../util';

const Point_crossProductZ = Point.crossProductZ, Point_dotProduct = Point.dotProduct;

export const Boundary = OwnedObject.extend({ typeName: 'Boundary' });

Boundary.fromXml = function(node, tag) { return Xml.getObject(node, this, tag || 'Curve'); };
Boundary.parseXml = function( node ) {
  return new LazyBoundary(node).decode();
};

Boundary.arrayFromXml = function(parentNode, tag) {
  return map( Xml.children(parentNode,tag), Boundary.parseXml );
};


// From SCIS_Figures\lineal_pos.cs

export const LinealPos = function (arcLen, point, pieceIdx) {
  this.arcLen = arcLen;
  this.point = point;
  this.pieceIdx = pieceIdx;
};

// Segment
//--------------------------------------------------------------------------------

export const Segment = function (P0, P1) {
  this.P0 = P0;
  this.P1 = P1;
};
Segment.prototype = {
  constructor: Segment,
  type: 'Segment',
  clone() {
    return new Segment(this.P0.clone(), this.P1.clone());
  },
  isEqual( other ) {
    return other instanceof Segment &&
        this.P0.isEqual(other.P0) &&
        this.P1.isEqual(other.P1);
  },
  last( v ) {
    return v === undefined ? this.P1 : (this.P1 = v, this);
  },
  get bounds() {
    return segmentBounds(this.P0, this.P1);
  },
  boundsAfter(t) {
    return segmentBounds(t.apply(this.P0), t.apply(this.P1));
  },
  transform( op ) {
    this.P0.transform(op);
    this.P1.transform(op);
  },
  chainedTransform( op ) {
    this.P1.transform(op);
  },
  chainedDraw(context, transform ) {
    const tP1 = transform ? transform.apply(this.P1) : this.P1;
    context.lineTo(tP1.x, tP1.y);
  },
  chainedToSVG() {
    return " L" + this.P1.toSVG();
  },
  chainedToXmlString() {
    return this.P0.toXmlString();
  },
  chainedPoints(points) { points.push(this.P0); },
  toXmlString() { return this.P0.toXmlString() + ',' + this.P1.toXmlString(); },
  toCubic() {
    return Cubic.fromSegment(this.P0.clone(), this.P1.clone());
  },
  draw(context, transform ) {
    const tP0 = transform ? transform.apply(this.P0) : this.P0;
    context.moveTo(tP0.x, tP0.y);
    this.chainedDraw(context, transform);
  },
  eval(t) {
    return Point.FromPolygon(t, this.P0, this.P1);
  },
  split(u) {
    const PC = Point.sm(this.P0, 1 - u).add(Point.sm(this.P1, u));
    return {
      left: new Segment(this.P0.clone(), PC),
      right: new Segment(PC.clone(), this.P1.clone())
    };
  },
  isFlatEnough2(/*threshold2*/) {
    return true;
  },
  arcLength() {
    return Point.distance(this.P0, this.P1);
  },
  dir() {
    const arcLen = this.arcLength();
    const dir = this.P1.minus(this.P0);
    if(arcLen > 0) {
      dir.sm(1 / arcLen);
    }
    return dir;
  },

  // The regular parameter (Time) of a straight line segment is its linear interpolation
  // parameter U=aL/Length.
  arcLenToTime(x) {
    const arcLen = this.arcLength();
    return arcLen > 0 ? x / arcLen : 0;
  },

  at(x) {
    return this.eval(this.arcLenToTime(x));
  },

  extendedAt(x) {
    return this.eval(this.arcLenToTime(x));
  },

  tangentAt( /*x*/) {
    return this.dir();
  },

  pApprox() {
    const P0 = this.P0, P1 = this.P1;
    const arcLen = this.arcLength();
    return {
      points: [P0, P1],
      u: [0, 1],
      l: [0, arcLen],
      arcLen: arcLen
    };
  },
  reversed() {
    return new Segment(this.P1.clone(), this.P0.clone());
  },
  polygonize( /*flatness, minPoints*/) {
    return [this.P0.clone(), this.P1.clone()];
  }
};

// Quadratic
//--------------------------------------------------------------------------------

export const Quadratic = function (P0, P1, P2) {
  this.P0 = P0;
  this.P1 = P1;
  this.P2 = P2;
};
Quadratic.prototype = {
  constructor: Quadratic,
  type: 'Quadratic',
  clone() {
    return new Quadratic(this.P0.clone(), this.P1.clone(), this.P2.clone());
  },
  isEqual( other ) {
    return other instanceof Quadratic,
        this.P0.isEqual(other.P0),
        this.P1.isEqual(other.P1),
        this.P2.isEqual(other.P2);
  },
  last( v ) {
    return v === undefined ? this.P2 : (this.P2 = v, this);
  },
  chainedTransform( op ) {
    this.P1.transform(op);
    this.P2.transform(op);
  },
  chainedDraw(context, transform ) {
    const tP1 = transform ? transform.apply(this.P1) : this.P1,
      tP2 = transform ? transform.apply(this.P2) : this.P2;
    context.quadraticCurveTo(tP1.x, tP1.y, tP2.x, tP2.y);
  },
  chainedToSVG() {
    return " Q" + this.P1.toSVG() + " " + this.P2.toSVG();
  },
  chainedToXmlString() {
    return this.P0.toXmlString() + ',' + this.P1.toXmlString();
  },
  chainedPoints(points) {
    points.push(this.P0);
    points.push(this.P1);
  },
  toXmlString() { return this.chainedToXmlString() + ',' + this.P2.toXmlString(); },
  draw(context, transform ) {
    const tP0 = transform ? transform.apply(this.P0) : this.P0;
    context.moveTo(tP0.x, tP0.y);
    this.chainedDraw(context, transform);
  },
  reversed() {
    return new Quadratic(this.P2.clone(), this.P1.clone(), this.P0.clone());
  },

  get bounds() {
    let p0 = this.P0
    let lv12 = this.P1.clone()
    lv12 = lv12.scalarMultiply(2)
    let p1 = Point.add(this.P0, lv12).scalarMultiply(1.0/3.0)
    let p2 = Point.add(this.P2, lv12).scalarMultiply(1.0/3.0)
    let p3 = this.P2
    
    return bezierBounds(p0,p1,p2,p3);
  },

  boundsAfter(t) {
    
    let p0 = this.P0
    let lv12 = this.P1.clone()
    lv12 = lv12.scalarMultiply(2)
    let p1 = Point.add(this.P0, lv12).scalarMultiply(1.0/3.0)
    let p2 = Point.add(this.P2, lv12).scalarMultiply(1.0/3.0)
    let p3 = this.P2

    return bezierBounds(t.apply(p0), t.apply(p1), t.apply(p2), t.apply(p3));
  },
  /*,
eval( t ) {
console.log('[TODO] Quadratic eval');
},
split( u ) {
console.log('[TODO] Quadratic split');
},
isFlatEnough2( threshold2) {
console.log('[TODO] Quadratic isFlatEnough2');
},
at( x ) {
console.log('[TODO] Quadratic at');
},
extendedAt( x ) {
console.log('[TODO] Quadratic extendedAt');
},
tangentAt( x ) {
console.log('[TODO] Quadratic tangentAt');
},
polygonize( flatness,minPoints) {
...
},
*/
};

// Cubic
//----------------------------------------------------------------------------------------------
// Defined in Bezier.js

// Curve
//----------------------------------------------------------------------------------------------

export const Curve = Boundary.extend({ typeName: 'Curve',
  init(pieces, closed) {
    this.pieces = pieces;
    this.closed = closed;
  },
  clone() {
    const copiedPieces = [];
    const pieces = this.pieces;
    copiedPieces[0] = pieces[0].clone();
    for( let i = 1; i < pieces.length; ++i) {
      copiedPieces[i] = pieces[i].clone();
      copiedPieces[i].P0 = copiedPieces[i - 1].last();
    }
    if(this.closed) {
      copiedPieces[0].P0 = copiedPieces[pieces.length - 1].last();
    }
    return new Curve(copiedPieces, this.closed);
  },
  transform( op ) {
    const pieces = this.pieces;
    if(!this.closed) { pieces[0].P0.transform(op); }
    //_.forEach(pieces,function(p) {
    for( let k = 0, kEnd = pieces.length; k < kEnd; ++k) {
      const p = pieces[k];
      p.chainedTransform(op);
    }//);
    return this;
  },
  transformed( op ) { return this.clone().transform(op); },

  // unrolled performant version
  get bounds() {
    // return Bounds.ofThings(this.pieces);

    const pieces = this.pieces;
    const count = pieces.length;
    if(count === 0) {
      return null;
    }

    let b = pieces[0].bounds;
    if(count === 1) {
      return b;
    }

    let lx = b.lx, ly = b.ly,
        hx = b.hx, hy = b.hy;
    for( let k = 1; k < count; ++k) {
      const p = pieces[k];
      if(p.P2) { // quadratic and cubic beziers pieces
        b = p.bounds;
        if(lx > b.lx) { lx = b.lx; }
        if(hx < b.hx) { hx = b.hx; }
        if(ly > b.ly) { ly = b.ly; }
        if(hy < b.hy) { hy = b.hy; }
      }
      else { // segment piece
        const P1 = p.P1;
        const P1x = P1.x, P1y = P1.y;
        if(lx > P1x) { lx = P1x; }
        else if(hx < P1x) { hx = P1x; }
        if(ly > P1y) { ly = P1y; }
        else if(hy < P1y) { hy = P1y; }
      }
    }
    return new Bounds(lx, ly, hx, hy);
  },
  boundsAfter(t) {
    // return Bounds.ofThingsAfter(this.pieces,t);

    const pieces = this.pieces;
    const count = pieces.length;
    if(count === 0) {
      return null;
    }
    let b = pieces[0].boundsAfter(t);
    if(count === 1) {
      return b;
    }

    let lx = b.lx, ly = b.ly,
        hx = b.hx, hy = b.hy;
    for( let k = 1; k < count; ++k) {
      const p = pieces[k];
      if(p.P2) { // quadratic and cubic beziers pieces
        b = p.boundsAfter(t);
        if(lx > b.lx) { lx = b.lx; }
        if(hx < b.hx) { hx = b.hx; }
        if(ly > b.ly) { ly = b.ly; }
        if(hy < b.hy) { hy = b.hy; }
      }
      else { // segment piece

        // [Inline:Matrix.apply]
        // (tP1x,tP1y) = t.apply(p.P1);
        const P1 = p.P1;
        const P1x = P1.x, P1y = P1.y;
        const tP1x = P1x * t.s00 + P1y * t.s01 + t.dx;
        const tP1y = P1x * t.s10 + P1y * t.s11 + t.dy;

        if(lx > tP1x) { lx = tP1x; }
        else if(hx < tP1x) { hx = tP1x; }
        if(ly > tP1y) { ly = tP1y; }
        else if(hy < tP1y) { hy = tP1y; }
      }
    }
    return new Bounds(lx, ly, hx, hy);
  },

  draw(context, transform ) {
    const pieces = this.pieces;
    const p0 = transform ? transform.apply(pieces[0].P0) : pieces[0].P0;
    context.moveTo(p0.x, p0.y);
    //_.forEach(pieces,function(p) {
    for( let k = 0, kEnd = pieces.length; k < kEnd; ++k) {
      const p = pieces[k];
      p.chainedDraw(context, transform);
    }//);
    if(this.closed) {
      context.closePath();
    }
  },
  toSVG() {
    const pieces = this.pieces;
    let svgCurve = 'M' + pieces[0].P0.toSVG();
    //_.forEach(this.pieces,function(p) {
    for( let k = 0, kEnd = pieces.length; k < kEnd; ++k) {
      const p = pieces[k];
      svgCurve += p.chainedToSVG();
    }//);
    if(this.closed) {
      svgCurve += 'Z';
    }
    return svgCurve;
  },
  getFirstPoint() { return this.pieces[0].P0; },
  buildXml(node, tag) { Xml.addObject(node, this, tag || 'Curve'); },
  setupXml( node ) {
    if(!this.closed) {
      Xml.setBoolAttr(node, 'IsClosed', this.closed);
    }
    let xmlCurve = '';
    const lmo = this.pieces.length - 1;
    for( let k = 0; k < lmo; k++) {
      xmlCurve += this.pieces[k].chainedToXmlString() + '|';
    }
    if(this.closed) {
      // In closed curves, the last point is equal to the first one
      xmlCurve += this.pieces[lmo].chainedToXmlString();
    }
    else {
      // In open curves, we need the last point
      xmlCurve += this.pieces[lmo].toXmlString();
    }
    node.setAttribute('Points',xmlCurve);
    node.setAttribute('Type','Bezier3');
  },
  asPieces() {
    return this.pieces;
  },
  controlPoints() {
    const pieces = this.pieces;
    // Returns a polygon with all the control points
    // return _.map(pieces,function(p) { p.chainedPoints(hull); });
    const hull = [];
    for( let k = 0, kEnd = pieces.length; k < kEnd; ++k) {
      const p = pieces[k];
      p.chainedPoints(hull);
    }
    return hull;
  },
  tieExtremePoints() {
    for( let i = 1; i < this.pieces.length; i++) {
      this.pieces[i - 1].last(this.pieces[i].P0);
    }
    if(this.closed) {
      this.pieces[this.pieces.length - 1].last(this.pieces[0].P0);
    }
  },
  isEqual( other ) {
    return other instanceof Curve &&
        this.closed === other.closed &&
        areEqual(this.pieces, other.pieces);
  },
  splitPiece(pi, t) {
    const p = this.pieces[pi];
    const np = p.split(t);
    this.pieces.splice(pi, 1, np.left, np.right);
    this.tieExtremePoints();
  },
  arcLength() {
    let len = 0;
    const pieces = this.pieces;
    for( let k = 0, kEnd = pieces.length; k < kEnd; ++k) {
      len += pieces[k].arcLength();
    }
    return len;
  },
  polygonRep() {
    const points = [];
    const pieces = this.pieces;
    for( let k = 0, kEnd = pieces.length; k < kEnd; ++k) {
      const pApproxPoints = pieces[k].pApprox().points;
      const nEnd = pApproxPoints.length - (k === kEnd - 1 ? 0 : 1);
      for( let n = 0; n < nEnd; ++n) {
        points.push(pApproxPoints[n]);
      }
    }
    return points;
  },
  polygonize(flatness, minPoints) {
    const points = [];
    const pieces = this.pieces;
    for( let k = 0, kEnd = pieces.length; k < kEnd; ++k) {
      const piecePoints = pieces[k].polygonize(flatness, minPoints);
      const nEnd = piecePoints.length - (k === kEnd - 1 ? 0 : 1);
      for( let n = 0; n < nEnd; ++n) {
        points.push(piecePoints[n]);
      }
    }
    return points;
  },
  segmentIntersectionPos(from, to) {
    const pieces = this.pieces;
    let minDist = Number.MAX_VALUE;
    let minPos = null;
    const toFrom = to.minus(from);
    const segmentNorm = toFrom.norm();
    const dir = Point.sm(toFrom, 1 / segmentNorm);
    const dirP = dir.perpendicular();

    let arcLen = 0;

    // We can handle segments more efficiently here, it is not that much needed
    // for FitOnPath so we can leave it for later

    // We could also reject pieces based on their bounds. For the time being,
    // this is already quite fast

    for( let k = 0, kEnd = pieces.length; k < kEnd ; ++k) {
      const piece = pieces[k];
      const pApprox = piece.pApprox();
      const pApproxPoints = pApprox.points;

      for( let i = 0, iEnd = pApproxPoints.length - 1; i < iEnd; ++i) {
        const as = pApproxPoints[i],
          at = pApproxPoints[i + 1];
        const bs = from,
          bt = to;

        const adir = at.minus(as),
        bdir = bt.minus(bs),
        det = Point_crossProductZ(adir, bdir);
        if(det !== 0) {
          const d = bs.minus(as),
          deta = Point_crossProductZ(d, bdir),
          detb = Point_crossProductZ(d, adir),
          ttb = detb / det;

          if(ttb >= 0 && ttb <= 1) {

            const tta = deta / det;
            const tta_0 = tta >= 0,
              tta_1 = tta <= 1;

            if(tta_0 && tta_1) {
              // Found intersection... done...
              // console.log('Intersection '+tta);

              return new LinealPos(arcLen + (pApprox.l[i] * (1 - tta) + pApprox.l[i + 1] * tta), Point.interpolate(as, at, tta), k);
            }

            let pk = null;
            if( !tta_0 && tta > -0.2 ) {
              // console.log('Almost << '+tta);
              pk = as;
            }
            else if(!tta_1 && tta < 1.2) {
              // console.log('Almost >> '+tta);
              pk = at;
            }

            if(pk) {
              // Best distance from point to segment
              const distance = Math.abs(Point_dotProduct(d, dirP));
              if(distance < minDist) {
                minDist = distance;
                minPos = new LinealPos(arcLen + pApprox.l[i + (!tta_0 ? 1 : 0)], pk, k);
              }
            }
          }
        }
      }

      arcLen += pApprox.arcLen;
    }

    return minPos;
  },
  reversed() {
    const pieces = this.pieces;
    const reversedPieces = [];
    for( let k = pieces.length - 1; k >= 0; --k) {
      reversedPieces.push(pieces[k].reversed());
    }
    return Curve.fromNonTiedPieces(reversedPieces, this.closed);
  },
  // Return CW or CCW
  orientation() {
    return computeOrientation(this.controlPoints());
  }
});

Curve.fromXmlString = function (curveString, closed) {

  const piecesString = curveString.split("|");

  // const rawPieces = _.map(piecesString,_pointArrayFromXmlString);
  const rawPieces = [];
  for( let k = 0, kEnd = piecesString.length; k < kEnd; ++k) {
    const piece = piecesString[k];
    rawPieces.push(_pointArrayFromXmlString(piece));
  }

  // Pieces share the extreme points, handle with care

  const pieces = [];
  const lastPieceIndex = rawPieces.length - 1;
  for( let i = 0, iEnd = rawPieces.length; i < iEnd; ++i) {

    const lastPoint = (i === lastPieceIndex) ? closed ? rawPieces[0][0]
      : rawPieces[lastPieceIndex][rawPieces[lastPieceIndex].length - 1]
      : rawPieces[i + 1][0];
    const currentRawPiece = rawPieces[i];
    
    if (i != lastPieceIndex || closed) 
        currentRawPiece.push(lastPoint)

    const pieceType = currentRawPiece.length 

      switch (pieceType) {
        case 2:
          pieces.push(new Segment(currentRawPiece[0], currentRawPiece[1]));
          break;
        case 3:
          pieces.push(new Quadratic(currentRawPiece[0], currentRawPiece[1], currentRawPiece[2]));
          break;
        case 4:
          pieces.push(new Cubic(currentRawPiece[0], currentRawPiece[1], currentRawPiece[2], currentRawPiece[3]));
          break;
        default:
          // This should never happen, but if we have data corruption lets just draw a segment
          // to at least show something and avoid breaking the app
  
          // Ignore the piece
          break;
      }
    }
    
    return new Curve(pieces, closed);
  },

  
Curve.fromNonTiedPieces = function (pieces, closed) {
  if( closed && pieces.length > 0 ) {
    const P0 = pieces[0].P0;
    const PE = last(pieces).last();
    if( ! P0.isNear(PE) ) {
      pieces.push( new Segment(PE,P0) );
    }
  }
  const curve = new Curve(pieces, closed);
  curve.tieExtremePoints();
  return curve;
};


// Polyline
//---------------------------------------------------------------------------

const _pointArrayFromXmlString = function (s) {
  const sepPoints = s.split(',');
  return map(sepPoints, Point.fromXmlString);
};

const _pointArrayToXmlString = function (pa) {
  const vals = map(pa, function (v) { return v.toXmlString(); });
  return vals.join(',');
};

export const Polyline = Boundary.extend({ typeName: 'Polyline',
  init(points, closed) {
    this.points = points;
    this.closed = closed;
  },
  clone() {
    return new Polyline(cloneDeep(this.points), this.closed);
  },
  transform( op) {
    const points = this.points;
    // invokeMap(points,'transform',op);
    for( let k = 0, kEnd = points.length; k < kEnd; ++k) {
      points[k].transform(op);
    }
    return this;
  },
  transformed( op ) { return this.clone().transform(op); },
  get bounds() {
    return Bounds.ofPoints(this.points);
  },
  boundsAfter(transform) {
    return Bounds.ofPointsAfter(this.points, transform);
  },

  draw(context, transform ) {
    const points = this.points;
    const length = points.length;
    const tp0 = transform.apply(points[0]);
    context.moveTo(tp0.x, tp0.y);
    for( let i = 1; i < length; ++i) {
      const tp = transform.apply(points[i]);
      context.lineTo(tp.x, tp.y);
    }
    if(this.closed) {
      context.closePath();
    }
  },
  toSVG() {
    const { points } = this;
    const { length } = points;
    let svgCurve = 'M' + points[0].toSVG();
    for( let i = 1; i < length; ++i) {
      svgCurve += ('L' + points[i].toSVG());
    }
    if(this.closed) {
      svgCurve += 'Z';
    }
    return svgCurve;
  },
  getFirstPoint() { return this.points[0]; },
  buildXml(node, tag) { Xml.addObject(node, this, tag || 'Curve'); },
  setupXml( node ) {
    if(!this.closed) {
      Xml.setBoolAttr(node, 'IsClosed', this.closed);
    }
    const xmlCurve = _pointArrayToXmlString(this.points);
    node.setAttribute('Points', xmlCurve);
    node.setAttribute('Type','Polyline');
  },
  asPieces() {
    const pieces = [];
    for( let k = 1; k < this.points.length; ++k) {
      pieces.push(new Segment(this.points[k - 1], this.points[k]));
    }
    if(this.closed) {
      pieces.push(new Segment(this.points[this.points.length - 1], this.points[0]));
    }
    return pieces;
  },
  convertToCurve() {
    const pieces = [];
    for( let k = 1; k < this.points.length; ++k) {
      pieces.push(new Segment(this.points[k - 1].clone(), this.points[k].clone()));
    }
    if(this.closed) {
      pieces.push(new Segment(this.points[this.points.length - 1].clone(), this.points[0].clone()));
    }
    return Curve.fromNonTiedPieces(pieces, this.closed);
  },
  controlPoints() {
    return this.points.slice(0);
  },
  isEqual( other ) {
    return other instanceof Polyline &&
        this.closed === other.closed &&
        areEqual(this.points, other.points);
  },
  range() {
    return new IndexRange(0, this.points.length - 1, this.closed);
  },
  arcLength() {
    const { points } = this;
    const { length } = points;
    let len = 0;
    for( let k = 1; k < length; ++k) {
      len += Point.distance(points[k], points[k - 1]);
    }
    return len;
  },
  polygonRep() {
    return this.points;
  },
  // Return 'CW' or 'CCW'
  orientation: function() {
    return computeOrientation(this.points);
  }
});

Polyline.fromXmlString = function (pointsString, closed) {
  const points = _pointArrayFromXmlString(pointsString);
  return new Polyline(points, closed);
};

// LazyBoundary
//----------------------------------------------------------------------------------

export const LazyBoundary = OwnedObject.extend({ typeName: 'LazyBoundary',
  init( node ) {
    this._points = node.getAttribute('Points');
    this._isClosed = Xml.getBoolAttr(node, 'IsClosed', true);
    this._type = node.getAttribute('Type') || 'Bezier3';
  },
  lazy: true,
  clone() {
    return this;
  },
  decode() {
    if(this._type === 'Polyline') {
      return Polyline.fromXmlString(this._points, this._isClosed);
    }
    else {
      return Curve.fromXmlString(this._points, this._isClosed);
    }
  },
  setupXml( node ) {
    if(!this._isClosed) {
      Xml.setBoolAttr(node, 'IsClosed', this._isClosed);
    }
    node.setAttribute('Points', this._points);
    node.setAttribute('Type', this._type);
  }
});


// From SCIS_Figures\index_range.cs

// enum Dir { Fwd, Bck }

// Encapsulates a closed range of valid indices [first,last], probably forming a cyclic sequence.<br>
// It provides index validation and checked methods for advancing a valid index in either direction.

/// first: First valid index on the range
/// last:  Last valid indes on the range
/// isCyclic: Specifies whether the range forms a cyclic sequence
const IndexRange = function( first, last, isCyclic ) {
  this.first = first;
  this.last = last;
  this.isCyclic = isCyclic;
};
IndexRange.prototype = {
  count() {
    return this.last - this.first + 1;
  },

  // Returns true if the index is inside the range.
  isValid(idx) {
    return idx >= this.first && idx <= this.last;
  },

  // Returns true if(idx-delta) is inside the range. This is always true if IsCyclic==true
  existPrev( idx, delta ) {
    return this.isCyclic ? true : (idx - (delta === undefined ? 1 : delta)) >= this.first;
  },

  // Returns true if(idx+delta) is inside the range. This is always true if IsCyclic==true
  existNext( idx, delta ) {
    return this.isCyclic ? true : (idx + (delta === undefined ? 1 : delta)) <= this.last;
  },

  // If idx-detla >= First, returns idx-delta; <br>
  // Else returns Last-(delta-(idx-First))+1.<br>
  // Precondition: IsValid(idx)<br>
  // Postcondition: IsValid(idx-delta)<br>
  // NOTE: If IsCyclic is false and idx==First, in Debug, this will fail the postcondition,
  // but in Release, it will return Last as if the idx were cyclic.<br>
  prev( idx, _delta ) {
    // g.assert( this.isValid(idx) );
    const delta = _delta === undefined ? 1 : _delta, first = this.first;
    return (idx - delta) >= first ? idx - delta : (this.isCyclic ? this.last - (delta - (idx - first)) + 1 : -1);
  },

  // If idx+delta &lt;= Last, returns idx+delta; <br>
  // Else returns First+(Delta-(Last-idx))-1.<br>
  // Precondition: IsValid(idx)<br>
  // Postcondition: IsValid(idx+delta)<br>
  // NOTE: If IsCyclic is false and idx==Last, in Debug, this will fail the postcondition,
  // but in Release, it will return First as if the idx were cyclic.<br>
  next( idx, _delta ) {
    // g.assert( this.isValid(idx) );
    const delta = _delta === undefined ? 1 : _delta, last = this.last;
    return (idx + delta) <= last ? idx + delta : (this.isCyclic ? this.first + (delta - (last - idx)) - 1 : -1);
  },

  distance( idx1, idx2 ) {
    return (!this.isCyclic) ? Math.abs(idx2 - idx1) :
        (idx1 <= idx2 ? idx2 - idx1 : ((this.last - idx1) + (idx2 - this.first) + 1));
  }
};

// [PERFORMANCE]
// Instead of creating new objects for the iteration, we could just modify it inplace
const Indexed = function (value, range) {
  this.value = value;
  this.range = range;
};
Indexed.prototype = {
  existPrev() {
    return this.range.existPrev(this.value);
  },
  existNext() {
    return this.range.existNext(this.value);
  },
  prev() {
    const { range } = this;
    return new Indexed(range.prev(this.value, 1), range);
  },
  next() {
    const { range } = this;
    return new Indexed(range.next(this.value, 1), range);
  }
};

// From Figure\curve_boundary_arc_len_cursor.cs

// Piecewise curve made of connected straight-line segments and bezier arcs (of degrees 0,1 and 3)

export const CurveArcLenCursor = function (curve) {
  this._curve = curve;
  this.reset();
};
CurveArcLenCursor.prototype = {
  type: 'CurveArcLenCursor',

  pos() {
    const currL = this._currL;
    const localL = currL - this._leftL;
    const point = this._piece.extendedAt(localL); // [TODO]
    return new LinealPos(currL, point, this._pieceIdx.value); // , new LinealPos(localL, point) ); Child not needed
  },

  tangent() {
    const piece = this._piece;
    let localL = this._currL - this._leftL;
    if(localL < 0) {
      localL = 0;
    }
    else {
      const pieceArcLength = piece.arcLength(); // [TODO]
      if(localL > pieceArcLength) {
        localL = pieceArcLength;
      }
    }
    return piece.tangentAt(localL); // [TODO]
  },

  reset() {
    const curve = this._curve;
    const pieceIdx = this._pieceIdx = new Indexed(0, new IndexRange(0, curve.pieces.length - 1, curve.closed));
    this._currL = 0;
    this._leftL = 0;
    const piece = this._piece = curve.pieces[pieceIdx.value];
    this._rightL = piece.arcLength();
  },

  move( delta ) {
    const newL = this._currL + delta, curve = this._curve;
    let rem = 0, pieceIdx = this._pieceIdx, piece;

    if( delta > 0 ) {
      while( newL > this._rightL ) {
        if( pieceIdx.existNext() ) {
          pieceIdx = this._pieceIdx = pieceIdx.next();
          piece = this._piece = curve.pieces[pieceIdx.value];
          this._leftL = this._rightL;
          this._rightL = this._leftL + piece.arcLength();
          // console.log( this._leftL + ' ' + this._rightL );
        }
        else {
          rem = newL - this._rightL;
          break;
        }
      }
    }
    else {
      while (newL < this._leftL) {
        if(pieceIdx.existPrev()) {
          pieceIdx = this._pieceIdx = pieceIdx.prev();
          piece = this._piece = curve.pieces[pieceIdx.value];
          this._rightL = this._leftL;
          this._leftL = this._rightL - piece.arcLength();
        }
        else {
          rem = this._leftL - newL;
          break;
        }
      }
    }

    this._currL = newL;
    return rem;
  }
};


Boundary.createRectangle = function( config = {} ) {
  let center, width, height;
  if( config.bounds || config instanceof Bounds ) {
    const bounds = config.bounds || config;
    center = bounds.center;
    width = bounds.width;
    height = bounds.height;
  }
  else {
    center = config.center || new Point(0, 0);
    width = config.width; height = config.height;
    if(width === undefined) { width = height; }
    if(height === undefined) { height = width; }
  }
  const p0 = Point.add(center, new Point(-width / 2, -height / 2)),
        p1 = Point.add(center, new Point(width / 2, -height / 2)),
        p2 = Point.add(center, new Point(width / 2, height / 2)),
        p3 = Point.add(center, new Point(-width / 2, height / 2));
  const curve = new Curve([new Segment(p0, p1), new Segment(p1, p2), new Segment(p2, p3), new Segment(p3, p0)], true);
  return curve;
};

Boundary.createEllipse = function( config = {} ) {
  let center, width, height;
  if(config.bounds || config instanceof Bounds) {
    const bounds = config.bounds || config;
    center = bounds.center;
    width = bounds.width;
    height = bounds.height;
  }
  else {
    center = config.center || new Point(0, 0);
    width = config.width; height = config.height;
    if( width === undefined ) {
      width = height;
    }
    if( height === undefined ) {
      height = width;
    }
  }

  const s = 0.5522911793037017;

  const p0 = Point.add(center, new Point(-width / 2, 0));
  const q01 = Point.add(center, new Point(-width / 2, -height / 2 * s));
  const q02 = Point.add(center, new Point(-width / 2 * s, -height / 2));

  const p1 = Point.add(center, new Point(0, -height / 2));
  const q11 = Point.add(center, new Point(width / 2 * s, -height / 2));
  const q12 = Point.add(center, new Point(width / 2, -height / 2 * s));

  const p2 = Point.add(center, new Point(width / 2, 0));
  const q21 = Point.add(center, new Point(width / 2, height / 2 * s));
  const q22 = Point.add(center, new Point(width / 2 * s, height / 2));

  const p3 = Point.add(center, new Point(0, height / 2));
  const q31 = Point.add(center, new Point(-width / 2 * s, height / 2));
  const q32 = Point.add(center, new Point(-width / 2, height / 2 * s));

  const curve = new Curve([
    new Cubic(p0, q01, q02, p1),
    new Cubic(p1, q11, q12, p2),
    new Cubic(p2, q21, q22, p3),
    new Cubic(p3, q31, q32, p0)], true);
  return curve;
};


function computeOrientation( points ) {
  var a = 0;
  for( var k = 0, kEnd = points.length-1; k < kEnd; ++k ) {
    var p1 = points[k  ],
        p2 = points[k+1];
    a += (p2.x - p1.x) * (p2.y + p1.y);
  }
  return a < 0 ? 'CCW' : 'CW';
}



/** WEBPACK FOOTER **
 ** ./src/cdl/geometry/curve.js
 **/